import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CustMode,
  SalaryApiResponse,
  ExpenseGroupResponse, InternalTimeLockSummaryPerson,
  ExpensePayloadUnion,
} from '../../features/ng-salary/ng-salary.interfaces';

export interface SalaryParams {
  subTotal?: boolean;
  sum?: string;
  custMode?: CustMode;
  per?: string;
  type?: string;
  id?: number;
  from?: string;
  to?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SalaryService {
  constructor(@Inject(HttpClient) private http: HttpClient) {}

  get<T = SalaryApiResponse>(params: SalaryParams): Observable<T[]> {
    // Construct URL manually to match legacy: /api/salary/:id/:from/:to
    // Query params: subTotal, sum, custMode, per, type
    // Legacy $resource: "api/salary/:id/:from/:to"
    const id = params.id !== undefined ? params.id : 0;
    const url = `/api/salary/${id}/${params.from}/${params.to}`;

    // Clean params for query string
    const queryParams: any = {};
    if (params.subTotal !== undefined) queryParams.subTotal = params.subTotal;
    if (params.sum !== undefined) queryParams.sum = params.sum;
    if (params.custMode !== undefined) queryParams.custMode = JSON.stringify(params.custMode);
    if (params.per !== undefined) queryParams.per = params.per;
    if (params.type !== undefined) queryParams.type = params.type;

    return this.http.get<any[]>(url, { params: queryParams });
  }

  /** Hämtar full payload (inkl. bilder) för ett enskilt utlägg, on-demand. */
  getExpensePayload(type: 'expense' | 'mileage' | 'allowance', id: number): Observable<ExpensePayloadUnion> {
    return this.http.get<any>(`/api/salary/expense-payload/${type}/${id}`);
  }

  previewInternalTimeLock(from: string, to: string): Observable<InternalTimeLockSummaryPerson[]> {
    return this.http.get<InternalTimeLockSummaryPerson[]>(`/api/salary/internal-lock-summary/${from}/${to}`);
  }

  lockInternalTime(from: string, to: string): Observable<{ ok: true }> {
    return this.http.post<{ ok: true }>(`/api/salary/0/${from}/${to}`, {});
  }

  save(data: { data: string }): Observable<SalaryApiResponse[]> {
    // Legacy: save: { method: "POST", isArray: true }
    // URL: /api/salarystatement/:id (Wait, SalaryStatement is different factory)
    // Legacy Salary factory: lock: { method: "POST", isArray: true }
    // But check usage in SalaryCtrl: Salary.save... Wait, Line 616: Salary.save({ data: JSON.stringify(sal_statement) }...)
    // Wait, Salary factory definition (Line 129 in restServices.js) only has get and lock.
    // SalaryStatement factory (Line 172) has get and save.
    // In SalaryCtrl line 616 it calls Salary.save.
    // Is it possible SalaryCtrl injects SalaryStatement as 'Salary'?
    // Line 38: Salary injection.
    // Let's re-read SalaryCtrl dependencies.
    // SalaryCtrl.$inject = [..., "Salary", ...].
    // Line 129: factory("Salary", ...). has get, lock.
    // Line 616: Salary.save(...).
    // This implies Salary factory HAS save method? Or I missed it in restServices.js.

    // Re-checking restServices.js view.
    // Line 129: factory("Salary", ...).
    // { get, lock }.
    // It DOES NOT have save.
    // Is there a typo in legacy code or did I miss something?
    // Maybe `Salary` resource default actions? $resource defaults are get, save (POST), query, remove, delete.
    // Custom actions { get: ..., lock: ... } extend defaults?
    // Yes, $resource second argument is paramDefaults, third is actions.
    // If specific actions are provided, they extend/override defaults?
    // "The action methods on the class object or instance object can be invoked... The default set contains these actions: { 'get': {method:'GET'}, 'save': {method:'POST'}, ... }"
    // BUT! "If the third argument is supplied, it extends the default actions."
    // So 'save' (POST) is available by default if not overridden.
    // The URL for default save would be "api/salary/:id/:from/:to".

    // Wait, look at line 616: Salary.save({ data: JSON.stringify(sal_statement) }...)
    // It posts to `api/salary/...`
    // Legacy code line 616: `Salary.save({ data: JSON.stringify(sal_statement) }, ...)`

    return this.http.post<any[]>(`/api/salary`, data);
  }
}
